import React, { useEffect, useState } from 'react'
import Nav from 'react-bootstrap/Nav'
import classNames from 'classnames'

import Highlighted from './highlighted'
import NavigationItems from './navigation-items'
import NavigationLinks from './navigation-links'
import { useMenuContext } from './useMainMenu'

import TextElement from '@/components/text-element/text-element'
import { resolvePctLinkUrl } from '@/internal/utils/resolve-pctlink-url'
import Link from '@/components/link/link'

const MenuMain = ({ items }) => {
  const [selectedDropdown, setSelectedDropdown] = useState(false)
  const [idDropwdown, setIdDropdown] = useState('')
  const { location } = useMenuContext()
  useEffect(() => {
    window.addEventListener('scroll', function () {
      setIdDropdown('')
      const mainHeader = document.querySelector('#header')
      if (mainHeader !== null) {
        if (window.pageYOffset > 10) {
          document.querySelector('#header').classList.add('small-header')
        } else {
          document.querySelector('#header').classList.remove('small-header')
        }
      }
    })
  }, [])

  const handleMenuMobile = (e, currentDropdown, closeSelf) => {
    if (closeSelf) {
      setSelectedDropdown(!selectedDropdown)
      setIdDropdown(currentDropdown)
    } else {
      setSelectedDropdown(true)
      setIdDropdown(currentDropdown)
    }
  }

  const handleMenuNav = (e, currentDropdown) => {
    if (e.target.classList.contains('selected')) {
      setIdDropdown('')
    } else {
      setIdDropdown(currentDropdown)
    }
    e.stopPropagation()
  }

  const handleClickOutside = () => {
    setIdDropdown('')
  }

  const useOutsideClick = (callback) => {
    const ref = React.useRef()

    React.useEffect(() => {
      const handleClick = (event) => {
        callback()
      }
      document.addEventListener('click', handleClick)
      return () => {
        document.removeEventListener('click', handleClick)
      }
    }, [])

    return ref
  }

  useOutsideClick(handleClickOutside)

  return (
    <Nav className="ms-auto" activeKey={location?.pathname} data-cy="menu">
      {items?.map((item) => {
        const currentId = item.codeId

        function isMainLinkActive(parentId, parentUrl, childUrl) {
          // Hack to activate all thought-leadership content
          if (
            parentId === 'navigation-item-thought-leadership' &&
            childUrl.match(
              /^\/(topic|thought-leadership|paper|dialog|post|paper)/
            ) !== null
          ) {
            return true
          }
          parentUrl = parentUrl?.endsWith('/') ? parentUrl : parentUrl + '/'
          childUrl = childUrl?.endsWith('/') ? childUrl : childUrl + '/'

          return childUrl.startsWith(parentUrl)
        }

        const childItems =
          item.navigationItems.length > 0
            ? item.navigationItems
            : item.navigationLinks
        const isCurrentId = idDropwdown === currentId
        const classShow = isCurrentId ? 'active-dropdown' : ''
        const classShowMobile =
          isCurrentId && selectedDropdown ? 'active-dropdown-mobile' : ''
        const childItemsMainLink = childItems[0]?.mainLink
          ? resolvePctLinkUrl(childItems[0]?.mainLink)
          : null
        const isActive = isMainLinkActive(
          item.codeId,
          childItemsMainLink,
          location?.pathname
        )
        const classIsSelected = isCurrentId ? 'selected' : ''
        const classSelectedMobile =
          isCurrentId && selectedDropdown ? 'selected-mobile' : ''
        const classIsActive = isCurrentId || isActive ? 'active' : ''

        if (
          item.navigationItems.length > 0 ||
          item.navigationLinks.length > 0
        ) {
          return (
            <NavItemDropdown
              key={currentId}
              onClick={(e) => {
                handleMenuNav(e, currentId)
              }}
            >
              <NavItemDropdownLabel
                className={[
                  classIsSelected,
                  classSelectedMobile,
                  classIsActive
                ]}
                onClick={(e) => {
                  handleMenuMobile(
                    e,
                    isCurrentId ? currentId : idDropwdown,
                    !!isCurrentId
                  )
                }}
              >
                {item.externalName}
              </NavItemDropdownLabel>
              <NavItemDropdownNavigation
                featured={item.featuredContent}
                mainLink={item.mainLink}
                className={[classShow, classShowMobile]}
                navigationItems={item.navigationItems}
                navigationLinks={item.navigationLinks}
              />
            </NavItemDropdown>
          )
        }

        return <NavItemLink key={currentId} link={item.mainLink} />
      })}
    </Nav>
  )
}

const NavItemLink = ({ link }) => {
  const { location } = useMenuContext()
  const otherClasses =
    location?.pathname === '/' &&
    link.codeId === 'insurtech-global-outlook-2023'
      ? 'active'
      : ''
  const url = resolvePctLinkUrl(link)
  return (
    <div className="menu-item">
      <Link
        to={url}
        className={classNames('nav-link', otherClasses)}
        partiallyActive
        activeClassName="active"
      >
        {link.externalName}
      </Link>
    </div>
  )
}

const NavItemDropdownLabel = ({ className, children }) => {
  return (
    <div
      data-cy="menu-item-dropdown-label"
      className={classNames('btn-reset', 'no-link dropdown', className)}
    >
      {children}
    </div>
  )
}
const NavItemDropdown = ({ onClick, children }) => {
  return (
    <button
      data-cy="menu-item-dropdown"
      className="btn-reset menu-item"
      onClick={onClick}
    >
      {children}
    </button>
  )
}
const NavItemDropdownNavigation = ({
  featured,
  mainLink,
  className,
  navigationItems,
  navigationLinks
}) => {
  const { handleMainMenu } = useMenuContext()
  const childItems =
    navigationItems.length > 0 ? navigationItems : navigationLinks
  const mainUrl = resolvePctLinkUrl(mainLink)
  return (
    <div
      className={classNames('dropdown-menu-main', className)}
      data-cy="menu-item-dropdown-items"
    >
      <div className="container-menu d-flex">
        {featured && <Highlighted highlighted={featured} />}
        <div
          className={`col-menus pt-lg-3 col-items ${
            childItems.length > 2 ? 'more-than-two' : ''
          }`}
        >
          {mainLink && (
            <div className="row mt-0">
              <TextElement type="h4" className="ps-0 pb-lg-4 pt-lg-4">
                <Link to={mainUrl} onClick={() => handleMainMenu()}>
                  {mainLink.externalName}
                </Link>
              </TextElement>
            </div>
          )}
          <div className={`row pb-1 pb-lg-5 d-flex items-${childItems.length}`}>
            {navigationItems.length > 0 && (
              <NavigationItems
                items={childItems}
                handleMainMenu={handleMainMenu}
              />
            )}
            {!navigationItems.length > 0 && (
              <NavigationLinks
                handleMainMenu={handleMainMenu}
                items={childItems}
                className="list-navigation-links"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
export default MenuMain
